import React, { useState, useEffect } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import {
  getClaimProgressNoteDocument,
  getPracticeEncounterListAICodingOptimization,
} from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import { DataGridCustomPagination } from "components/helpers";
import { columns } from "./helper";
import { SBContainer } from "./styles";

import ClaimSearch from "components/modals/claimSearch";
import DocumentViewer from "components/modals/claimDetails/documentViewer";
import EncounterDetails from "components/modals/encounterDetail";

const icdBg = "#e7f3fa";
const cptBg = "#f3e7fa";
const demoBg = "#f9fbfc";

const EncounterCoding = () => {
  const userData = useUserData();
  const token = userData?.access_token;
  const [rows, setRows] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openSearch, setOpenSearch] = useState(false);
  const [newPayload, setNewPayload] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const filteredRows =
    tabValue === 0
      ? rows
      : rows.filter((row) => {
          const raf = Number(row.raf_improvement_percentage || 0);
          const rvu = Number(row.rvu_improvement_percentage || 0);
          return raf > 0 || rvu > 0;
        });

  useEffect(() => {
    getClaimCodingList();
  }, [newPayload]);

  const getClaimCodingList = async () => {
    setLoading(true);
    try {
      const res = await getPracticeEncounterListAICodingOptimization(
        {
          practice_id: userData.selected_practice,
          user_id: userData?.user_details?.user_id,
          ...newPayload,
        },
        token
      );
      setRows(
        res.map((item) => ({
          ...item,
        }))
      );
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const getDocument = async (claim) => {
    setSnackbarSeverity("info");
    setSnackbarMessage("Getting Document...");
    setSnackbarOpen(true);
    try {
      const payload = {
        practice_id: userData?.selected_practice,
        claim_no: claim?.claim_no,
      };

      const blob = await getClaimProgressNoteDocument(payload, token);
      const mimeType = blob.type || "application/octet-stream";
      const url = URL.createObjectURL(blob);

      setDocumentUrl({ url, mimeType });
      setOpenDocDialog(true);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const handleCloseDocDialog = () => {
    setOpenDocDialog(false);
    if (documentUrl?.url && documentUrl?.url.startsWith("blob:")) {
      URL.revokeObjectURL(documentUrl.url);
    }
    setDocumentUrl(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleViewClick = (claim) => {
    setSelectedClaim(claim);
    setPopupOpen(true);
  };

  const handleOpenSearch = () => {
    setOpenSearch(true);
  };

  const handleCloseSearch = () => {
    setOpenSearch(false);
  };

  const handleClear = () => {
    setNewPayload(null);
  };

  return (
    <Box bgcolor="white" p={2}>
      <SBContainer>
        <Typography variant="h6" fontWeight={500}>
          Encounter Coding
        </Typography>
        <Box>
          {newPayload && (
            <Button variant="text" onClick={handleClear}>
              Clear
            </Button>
          )}
          <Button variant="outlined" onClick={handleOpenSearch}>
            Advance Search
          </Button>
        </Box>
      </SBContainer>
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="All" />
        <Tab label="Action Required" />
      </Tabs>
      <DataGrid
        rows={filteredRows}
        columns={columns(handleViewClick, getDocument)}
        autoHeight
        getRowId={(row) => row.claim_id}
        sx={{
          "& .demo-bg": { backgroundColor: demoBg },
          "& .icd-bg": { backgroundColor: icdBg },
          "& .cpt-bg": { backgroundColor: cptBg },
        }}
        loading={loading}
        slots={{ pagination: DataGridCustomPagination, toolbar: GridToolbar }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
      />
      <EncounterDetails
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        claim={selectedClaim}
        practiceID={userData.selected_practice}
        token={token}
        userData={userData}
      />
      <ClaimSearch
        open={openSearch}
        onClose={handleCloseSearch}
        setNewPayload={setNewPayload}
      />
      <DocumentViewer
        openDocDialog={openDocDialog}
        documentUrl={documentUrl}
        handleCloseDocDialog={handleCloseDocDialog}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EncounterCoding;
